import React, { useMemo, useState } from 'react';
import { AdminUserOut } from '../../services/apiClient';
import { fmtDate } from './format';

type Filter = 'all' | 'subscribed' | 'credit' | 'report' | 'none';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'subscribed', label: 'Subscribed' },
  { key: 'credit', label: 'Has credit' },
  { key: 'report', label: 'Has report' },
  { key: 'none', label: 'No access' },
];

interface Props {
  users: AdminUserOut[];
  selectedUserId: string | null;
  onSelect: (userId: string) => void;
}

function matchesFilter(user: AdminUserOut, filter: Filter): boolean {
  switch (filter) {
    case 'subscribed':
      return user.access !== null;
    case 'credit':
      return user.has_unused_credit;
    case 'report':
      return user.completed_reports_count > 0;
    case 'none':
      return (
        user.access === null &&
        !user.has_unused_credit &&
        user.completed_reports_count === 0 &&
        !user.last_purchase_at
      );
    default:
      return true;
  }
}

function statusLabel(user: AdminUserOut): string {
  if (user.access) return user.access;
  if (user.has_unused_credit) return 'credit ●';
  if (user.completed_reports_count > 0) return 'done';
  return '—';
}

const AdminUserTable: React.FC<Props> = ({ users, selectedUserId, onSelect }) => {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  // Filtering runs in the browser over the full list. At a few hundred users
  // this is instant and avoids a round-trip per keystroke.
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((u) => {
      if (!matchesFilter(u, filter)) return false;
      if (!needle) return true;
      return (
        u.email.toLowerCase().includes(needle) ||
        (u.name || '').toLowerCase().includes(needle)
      );
    });
  }, [users, query, filter]);

  return (
    <div className="admin-table-section">
      <div className="admin-controls">
        <input
          type="search"
          className="admin-search"
          placeholder="Search email or name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="admin-chips">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`admin-chip${filter === f.key ? ' admin-chip--active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Onboarding</th>
              <th>Status</th>
              <th>Reports</th>
              <th>Last Credit</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr
                key={u.id}
                tabIndex={0}
                aria-label={`Open detail for ${u.email}`}
                onClick={() => onSelect(u.id)}
                onKeyDown={(e) => {
                  // Rows are the only way into the detail drawer, so they must
                  // be reachable without a mouse. Deliberately no role="button"
                  // — that would flatten the row and hide the cell structure
                  // from screen readers.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(u.id);
                  }
                }}
                className={`admin-row${selectedUserId === u.id ? ' admin-row--selected' : ''}`}
              >
                <td>{u.name || '—'}</td>
                <td>{u.email}</td>
                <td>
                  <span className={`admin-badge admin-badge--${u.role}`}>{u.role}</span>
                </td>
                <td>{fmtDate(u.created_at)}</td>
                <td>{u.onboarding_status.replace(/_/g, ' ')}</td>
                <td>{statusLabel(u)}</td>
                <td>{u.completed_reports_count}</td>
                <td>{fmtDate(u.last_purchase_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && (
        <p className="admin-muted">No users match this search.</p>
      )}
    </div>
  );
};

export default AdminUserTable;
